import { Smile } from 'lucide-react'
import RichTextArea from 'components/RichTextArea'
import useActionGuard from 'hooks/useActionGuard'
import useVisualViewPort from 'hooks/useVisualViewPort'
import { useSwitchState } from 'hooks/useSwitchState'
import { EmojiPickerContent } from 'components/EmojiDropdown'
import { FC, PropsWithChildren, useMemo, useRef } from 'react'
import { cn, useIsMobile, Button, ButtonLoader } from '@wellesley/ui'

import AddMedia from '../../PostForm/AddMedia'
import { TPostForm } from '../../PostForm/types'
import SensitiveControl from '../../PostForm/ControlsBar/SensitiveControl'
import { useSubmitPost } from '../../PostForm/hooks/useSubmitPost'
import CurrentUserAvatar from '../../PostForm/CurrentUserAvatar'
import useAddEmojiHandler from '../../PostForm/hooks/useAddEmojiHandler'
import { PrivacyDropdown } from '../../PostForm/ControlsBar/PrivacyDropdown'
import getPrivacyOptions from '../../PostForm/helpers/privacy/getPrivacyOptions'
import { MediaContent } from '../../Feed/MediaContent'
import checkPrivacyOptionVariant from '../../PostForm/helpers/privacy/checkPrivacyOptionVariant'

const ModalContent: FC<PropsWithChildren & TPostForm> = (props) => {
  const {
    feed,
    isReply,
    submitButtonText,
    privacyOptionsVariant,
    isDisabledCommentOption = true,
  } = props
  const privacyOptions = useMemo(
    () =>
      getPrivacyOptions({
        variant: checkPrivacyOptionVariant(privacyOptionsVariant, isReply),
      }),
    [privacyOptionsVariant, isReply],
  )

  const {
    text,
    uploads,
    privacy,
    isFeedLoading,
    isSubmitLoading,
    isSensitive,
    canSubmitPost,
    contentWarning,
    isAllowedComments,
    setText,
    setPrivacy,
    addMedia,
    removeMedia,
    updateMedia,
    onAllowComments,
    handleEnterKeyDown,
    handleTextareaChange,
    handleSubmitPost,
    toggleSensitive,
    setContentWarning,
  } = useSubmitPost({
    ...props,
  })

  const { isDisabled, ActionTooltipWrapper } = useActionGuard()

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

  const onAddEmoji = useAddEmojiHandler({
    ref: textAreaRef,
    onSetText: setText,
  })

  const [isOpenEmojis, , , toggleEmojis] = useSwitchState()

  const isMobile = useIsMobile()

  const [viewPortHeight] = useVisualViewPort()

  const hasUploads = uploads.length > 0

  const [isOpenContentWarning, , , toggleOpenContentWarning] = useSwitchState()

  return (
    <div
      style={{
        height: viewPortHeight,
      }}
      className="flex flex-col bg-white"
    >
      <div className="gap-xxs px-s py-xs flex flex-1">
        <CurrentUserAvatar />
        <RichTextArea
          {...{ text, setText }}
          inputRef={textAreaRef}
          placeholder={
            props.isReply
              ? 'Type your reply here...'
              : 'Type your message here...'
          }
          autoFocus
          onKeyDown={handleEnterKeyDown}
          onChange={handleTextareaChange}
          className={cn(
            'h-full min-h-10 w-full resize-none leading-5 text-gray-700 outline-none',
          )}
        />
      </div>
      {hasUploads && (
        <MediaContent
          attachments={uploads}
          isSmallPreview={false}
          removeMedia={(id) => void removeMedia(id)}
          updateMedia={updateMedia}
          summary={contentWarning}
        />
      )}
      {props.children}
      <div
        className={cn(
          'h-l px-s py-xxs relative flex justify-between border-t border-gray-200',
          isSensitive && 'h-[104px] !pt-16',
        )}
      >
        {isSensitive && (
          <div className="absolute left-0 right-0 top-14 border border-dashed" />
        )}
        <div className="gap-s flex flex-1 items-center">
          {!isOpenContentWarning && (
            <div className="gap-s flex items-center">
              <Smile
                color={isOpenEmojis ? 'var(--blue-500)' : 'var(--gray-700)'}
                className="h-xs cursor-pointer"
                strokeWidth="2px"
                onClick={toggleEmojis}
              />
              <AddMedia
                uploads={uploads}
                onAddMedia={addMedia}
                isDisabled={false}
              />
              <PrivacyDropdown
                onPrivacyChange={setPrivacy}
                {...{
                  privacy,
                  privacyOptions,
                  onAllowComments,
                  isAllowedComments,
                  isDisabledCommentOption,
                }}
                isReply={isReply || (feed && feed.id !== feed.conversationId)}
              />
            </div>
          )}
          {hasUploads && (
            <SensitiveControl
              isMobile={isMobile}
              isSensitive={!!isSensitive}
              {...{
                toggleSensitive,
                contentWarning,
                setContentWarning,
                isOpenContentWarning,
                toggleOpenContentWarning,
              }}
            />
          )}
        </div>
        {!isOpenContentWarning && (
          <div className="gap-s flex items-center">
            <Button
              size="m"
              variant="tertiary"
              onClick={() => void props.onCloseModal?.()}
              className="min-w-[80px] p-0"
            >
              Cancel
            </Button>

            <ActionTooltipWrapper>
              <ButtonLoader
                type="submit"
                size="m"
                variant="primary"
                data-testid="submit-post"
                disabled={!canSubmitPost || isDisabled}
                isLoading={isSubmitLoading || isFeedLoading}
                className="min-w-[80px]"
                onClick={() => void handleSubmitPost()}
              >
                {submitButtonText}
              </ButtonLoader>
            </ActionTooltipWrapper>
          </div>
        )}
      </div>
      {isOpenEmojis && (
        <div className="[&_aside]:rounded-none [&_aside]:border-x-0 [&_aside]:border-b-0 [&_aside]:border-t">
          <EmojiPickerContent onEmojiClick={onAddEmoji} />
        </div>
      )}
    </div>
  )
}

export default ModalContent
